// El turno del TENS en una línea, para la tarjeta de su inicio (paso 2 de la
// interfaz por experiencia; Sebastián eligió la maqueta "Panorama" el
// 2026-10-16, docs/pitch/preview-turno-tens/).
//
// Lo que la tarjeta dice sale SOLO de catálogos y números: cuántos pacientes,
// el tipo de turno, a qué hora termina, el servicio y lo último que se
// registró (cama, nombre del cuidado o del registro, hora). Jamás texto libre:
// la evolución confirmada, la entrega y la observación de la piel no viajan
// aquí. Se lee el estado del dispositivo con la MISMA retención del hub
// (`cargarEstadoTens`): un turno que ya murió no se resume, se ofrece empezar.
//
// El tipo de turno y la hora de término se dicen SOLO si el calendario de la
// persona los confirma (revisión del paso 2, 2026-10-16). El turno guardado
// trae "Largo" por defecto aunque nadie lo haya elegido, y la hora fija de la
// retención (20:00 u 08:00) no es el horario de cada unidad: una noche de
// sábado que termina a las 09:00 o un turno diurno de APS mentían. Misma regla
// que TarjetaHoy: sin certeza no se afirma un turno. Pasado el término, la
// hora se calla.
import { cargarEstadoTens, claveEstadoTens } from './tensRegistro';
import { TURNO_24 } from './registroTurno';
import { CUIDADOS_SALA, servicioDe } from './tensServicios';
import { SERVICIO_OPTIONS } from './perfilCatalogos';

// El mismo vocabulario del calendario (TIPO_TURNO_LABEL de turnoCockpit), que
// es como el inicio nombra el turno en todas las capas.
const NOMBRE_TURNO = Object.freeze({ Largo: 'Turno largo', Noche: 'Turno de noche', [TURNO_24]: 'Turno de 24h' });
// El tipo del calendario que corresponde a cada turno del registro.
const TURNO_DEL_CALENDARIO = Object.freeze({ largo: 'Largo', noche: 'Noche', 24: TURNO_24 });

// Los registros del TENS con las palabras de los botones que los crean
// (HojaCuidadoRapido): lo que se tocó es lo que se lee.
const NOMBRE_REGISTRO = Object.freeze({
  cambio: 'Cambio de posición',
  cambio_no_realizable: 'No se pudo realizar el cambio',
  rotacion_cup: 'Rotación de CUP',
  signos: 'Signos vitales',
  piel: 'Piel al movilizar',
});

const NOMBRE_CUIDADO = Object.freeze(Object.fromEntries(CUIDADOS_SALA.map((c) => [c.id, c.label])));

// Solo claves propias del catálogo: un id adulterado como "constructor" no
// puede devolver una función heredada.
const deCatalogo = (mapa, clave) => (typeof clave === 'string' && Object.prototype.hasOwnProperty.call(mapa, clave) ? mapa[clave] : undefined);

const HORA_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;
const esMs = (n) => typeof n === 'number' && Number.isFinite(n) && n > 0;

// Un cuidado guarda solo "HH:MM", sin día. Para ordenarlo contra los registros
// (que traen su ts) se ubica en el turno: el mismo día en que empezó, o el
// siguiente si la hora quedó antes del comienzo (un turno de noche cruza la
// medianoche). El comienzo se toma al minuto: un cuidado marcado el mismo
// minuto en que se abrió la primera cama es de ese día, no del siguiente.
// Límite conocido y aceptado: en un turno de 24 horas, durante la gracia
// después del término (08:00 a 11:00 del día siguiente), un cuidado marcado a
// las 08:30 de ese día se lee como el de las 08:30 del primero, que es lo más
// probable; la card puede nombrar otro registro como el último.
function tsEnElTurno(hora, creado) {
  const m = typeof hora === 'string' ? HORA_RE.exec(hora) : null;
  if (!m || !esMs(creado)) return null;
  const d = new Date(creado);
  d.setHours(Number(m[1]), Number(m[2]), 0, 0);
  const inicio = new Date(creado);
  inicio.setSeconds(0, 0);
  if (d.getTime() < inicio.getTime()) d.setDate(d.getDate() + 1);
  return d.getTime();
}

function nombreServicio(id) {
  if (!id || id === 'otro') return null;
  return SERVICIO_OPTIONS.find((o) => o.value === id)?.label || null;
}

/**
 * Resume el turno ya cargado (`cargarEstadoTens`). Sin turno empezado o sin
 * camas devuelve null: la tarjeta ofrece empezar.
 * @param {object|null} estado
 * @param {{servicioPerfil?: string|null, calendario?: null|{tipo: string, horaFin?: string, fase?: string}}} opciones
 *   `servicioPerfil`: el del perfil, si el turno no eligió uno (se lee con
 *   `servicioDe`, la misma regla del hub). `calendario`: el turno vigente del
 *   calendario de la persona (`turnoVigente`), o null si no hay o es libre.
 * @returns {null | {nPacientes: number, turno: string|null, finHora: string|null,
 *   servicio: string|null, ultimo: null | {cama: string, que: string, hora: string}}}
 */
export function resumenTurnoTens(estado, { servicioPerfil = null, calendario = null } = {}) {
  if (!estado || !esMs(estado.creado)) return null;
  const camas = estado.camas && typeof estado.camas === 'object' ? estado.camas : {};
  const ids = Object.keys(camas).filter((c) => /^\d{1,4}$/.test(c));
  if (!ids.length) return null;

  let ultimo = null;
  const considerar = (ts, cama, que, hora) => {
    if (ts == null || !que || !HORA_RE.test(hora || '')) return;
    if (!ultimo || ts >= ultimo.ts) ultimo = { ts, cama, que, hora };
  };
  for (const cama of ids) {
    const reg = camas[cama] || {};
    for (const [id, hora] of Object.entries(reg.cuidados || {})) {
      considerar(tsEnElTurno(hora, estado.creado), cama, deCatalogo(NOMBRE_CUIDADO, id), hora);
    }
    for (const e of Array.isArray(reg.eventos) ? reg.eventos : []) {
      const ts = esMs(e?.ts) ? e.ts : tsEnElTurno(e?.hora, estado.creado);
      considerar(ts, cama, deCatalogo(NOMBRE_REGISTRO, e?.tipo), e?.hora);
    }
    // La evolución confirmada también es un registro: se nombra, su texto
    // jamás sale de la ficha.
    const evo = reg.evolucion?.confirmada;
    if (evo && esMs(evo.ts)) considerar(evo.ts, cama, 'Evolución confirmada', evo.hora);
  }

  const confirmado = !!calendario && deCatalogo(TURNO_DEL_CALENDARIO, calendario.tipo) === estado.turno;
  const enCurso = confirmado && calendario.fase !== 'cierre';
  return {
    nPacientes: ids.length,
    turno: confirmado ? deCatalogo(NOMBRE_TURNO, estado.turno) || null : null,
    finHora: enCurso && HORA_RE.test(calendario.horaFin || '') ? calendario.horaFin : null,
    servicio: nombreServicio(servicioDe(estado, { servicio: servicioPerfil })),
    ultimo: ultimo ? { cama: ultimo.cama, que: ultimo.que, hora: ultimo.hora } : null,
  };
}

/**
 * Lee el turno del TENS guardado en este dispositivo y lo resume. Solo lee: el
 * hub (HomeTens) es el único que escribe ese estado.
 */
export function leerResumenTurnoTens(userId, { ahora = Date.now(), servicioPerfil = null, calendario = null } = {}) {
  if (!userId) return null;
  let raw = null;
  try { raw = JSON.parse(localStorage.getItem(claveEstadoTens(userId))); } catch { raw = null; }
  return resumenTurnoTens(cargarEstadoTens(raw, { ahora }), { servicioPerfil, calendario });
}
